import moment from 'moment';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import type { FieldErrors } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { Spinner } from '@common/components/Spinner';
import { Routes } from '@common/constants';
import {
  getGenderOptionsForRestriction,
  normaliseGenderCode,
} from '@common/helpers/genderRestriction';
import {
  useBookingFormLabels,
  useCalendarNavigation,
  useGetSpaSettings,
  useGetSpaTreatment,
  useGetSpaTreatmentTimeslots,
  useGuestCountChange,
  useNotesPreferences,
  useSyncSpaDateToRedux,
  useTimeslotAvailability,
} from '@features/spa/hooks';
import { mapSlotsToTimeslotChips } from '@features/spa/utils/mapSlotsToTimeslotChips';
import { resolveSpaMaxGuests } from '@features/spa/utils/resolveSpaMaxGuests';
import { useTranslations } from '@lang';
import { basketSelectors } from '@store/basket/selectors';
import { yourJourneySelectors } from '@store/yourJourney/selectors';
import { buildExistingGuests } from '@utils/buildExistingGuests';
import { scrollToFirstError } from '@utils/scrollToFirstError';
import { useExistingGuestSelect } from '@utils/useExistingGuestSelect';
import { useRouter } from 'shared-routing';
import { Property } from 'shared-types/Property';

import type { SpaGuestConfig } from 'voyager';
import { SpaTreatmentFacilityBookingForm } from 'voyager';

import { Guest } from 'shared-types/Basket';
import { useSubmitTreatment } from './hooks/useSubmitTreatment';
import { SpaTreatmentFormValues } from './SpaTreatmentBookingV2.types';

type SpaTreatmentBookingV2Props = {
  id: number;
  property: Property;
  language: string;
  onClose: (showMiniBasket: boolean) => void;
  loggedInGuest?: Guest | null;
};

export const SpaTreatmentBookingV2: FC<SpaTreatmentBookingV2Props> = ({
  id,
  property,
  language,
  onClose,
  loggedInGuest,
}) => {
  const { t } = useTranslations();
  const router = useRouter();
  const journeyParams = useSelector(yourJourneySelectors.getParsedJourneyParams) as any;
  const isSubmitting = useSelector((state: Record<string, unknown>) =>
    basketSelectors.getIsBasketDetailsFetching(state)
  );
  const basketGuests = useSelector((state: any) => basketSelectors.getBasketGuests(state)) || [];

  const existingGuests = useMemo(
    () => buildExistingGuests(basketGuests, loggedInGuest),
    [basketGuests, loggedInGuest]
  );

  const form = useFormContext<SpaTreatmentFormValues>();
  const { errors: formErrors } = useFormState({ control: form.control });

  const { data: spaEntity, isFetching: isLoadingEntity } = useGetSpaTreatment(
    property.id,
    language,
    id,
    moment(journeyParams.spaDate).format('YYYY-MM-DD'),
    true,
    false,
    journeyParams.spaAdults
  );
  const { data: spaSettings } = useGetSpaSettings(property.id);
  const showGenderNeutral = spaSettings?.showGenderNeutral || false;

  const watchedGuests = useWatch({ control: form.control, name: 'guests' });
  const watchedDate = useWatch({ control: form.control, name: 'date' });
  const watchedArrivalTime = useWatch({ control: form.control, name: 'arrivalTime' });

  const selectedDateString = (
    watchedDate || moment(journeyParams.spaDate).format('YYYY-MM-DD')
  ).trim();

  useSyncSpaDateToRedux(selectedDateString, journeyParams.spaDate);

  const bookingLabels = useBookingFormLabels(language, {
    phoneNumber: spaSettings?.phoneNumber,
    entityName: spaEntity?.name,
    spaEntityLabel: t('spa.spaTreatment').toLowerCase(),
    pluralEntityLabel: t('app.spaTreatments'),
    formattedDate: moment(selectedDateString).format('dddd Do MMMM YYYY'),
    onCtaClick: () => router.push({ pathname: Routes.SPA_TREATMENT_LIST }),
  });

  const {
    weekStartDate,
    setWeekStartDate,
    calendarView,
    setCalendarView,
    handleCalendarViewChange,
    debouncedWeekStartDate,
  } = useCalendarNavigation({ initialDate: selectedDateString, form });

  const {
    data: rawTimeslots,
    isFetching: isLoadingTimeslots,
    isError: isTimeslotsError,
  } = useGetSpaTreatmentTimeslots(
    property.id,
    Number(id),
    debouncedWeekStartDate,
    language,
    undefined,
    calendarView === 'week',
    7
  );

  const minGuests = spaEntity?.minimumGuests || 1;
  const requestedGuests = journeyParams?.spaAdults || 1;
  const maxGuests = spaEntity ? resolveSpaMaxGuests(spaEntity, { requestedGuests }) : minGuests;

  const guestCount =
    watchedGuests?.length || Math.max(minGuests, Math.min(maxGuests, requestedGuests));

  const {
    slotsByDate,
    availability,
    handleDateSelect,
    hasNoAvailabilityAllWeek,
    hasNoAvailabilityToday,
  } = useTimeslotAvailability({
    rawTimeslots,
    selectedDateString,
    weekStartDate,
    debouncedWeekStartDate,
    isLoadingTimeslots,
    isTimeslotsError,
    form,
    setWeekStartDate,
    calendarView,
    setCalendarView,
    guestCount,
  });

  const selectedSlot = slotsByDate[selectedDateString]?.find((s) =>
    watchedArrivalTime?.startDateTime
      ? moment(s.startDateTime).isSame(moment(watchedArrivalTime.startDateTime), 'minute')
      : moment(s.startDateTime).format('HH:mm') === watchedArrivalTime?.startTime
  );
  const effectiveMaxGuests = spaEntity
    ? resolveSpaMaxGuests(spaEntity, {
        slotCapacity: selectedSlot?.numberAvailable,
        requestedGuests,
      })
    : minGuests;

  const timeslotChips = useMemo(
    () => mapSlotsToTimeslotChips(slotsByDate, selectedDateString, guestCount),
    [slotsByDate, selectedDateString, guestCount]
  );

  useEffect(() => {
    if (!watchedArrivalTime) return;
    const daySlots = slotsByDate[selectedDateString];
    const matching = daySlots?.find((s) =>
      watchedArrivalTime.startDateTime
        ? moment(s.startDateTime).isSame(moment(watchedArrivalTime.startDateTime), 'minute')
        : moment(s.startDateTime).format('HH:mm') === watchedArrivalTime.startTime
    );
    const stillAvailable =
      !!matching &&
      (typeof matching.numberAvailable !== 'number' || matching.numberAvailable >= guestCount);
    if (!stillAvailable) {
      form.setValue('arrivalTime', undefined, { shouldDirty: true });
    }
  }, [slotsByDate, selectedDateString, guestCount, watchedArrivalTime, form]);

  const defaultGender =
    spaEntity?.genderSpecific === 'M' ? 'male' : spaEntity?.genderSpecific === 'F' ? 'female' : '';

  const handleGuestCountChange = useGuestCountChange({ form, defaultGender });

  const targetGuestCount = Math.max(minGuests, effectiveMaxGuests);
  useEffect(() => {
    if (guestCount > targetGuestCount) {
      handleGuestCountChange(targetGuestCount);
    }
  }, [targetGuestCount, guestCount, handleGuestCountChange]);

  const guestFieldLabels = useMemo(
    () => ({
      title: t('common.title'),
      firstName: t('common.firstName'),
      lastName: t('common.lastName'),
      gender: t('common.gender'),
    }),
    [t]
  );

  const translateGuestErrors = useCallback(
    (guestErrors?: FieldErrors<SpaTreatmentFormValues['guests'][number]>) => {
      if (!guestErrors) return undefined;
      const messageFor = (field: 'title' | 'firstName' | 'lastName' | 'gender', label: string) =>
        guestErrors[field] ? t('error.fieldRequired', { label }) : undefined;
      const translated = {
        title: messageFor('title', guestFieldLabels.title),
        firstName: messageFor('firstName', guestFieldLabels.firstName),
        lastName: messageFor('lastName', guestFieldLabels.lastName),
        gender: messageFor('gender', guestFieldLabels.gender),
      };
      const hasAny = Object.values(translated).some(Boolean);
      return hasAny ? translated : undefined;
    },
    [t, guestFieldLabels]
  );

  const guestConfigs: SpaGuestConfig[] = useMemo(() => {
    const guests = watchedGuests || [];
    const guestErrorsArray =
      (formErrors.guests as unknown as FieldErrors<SpaTreatmentFormValues['guests'][number]>[]) ||
      [];
    return Array.from({ length: guestCount }, (_, i) => ({
      guestNumber: i + 1,
      values: {
        title: guests[i]?.title || '',
        firstName: guests[i]?.firstName || '',
        lastName: guests[i]?.lastName || '',
        gender: guests[i]?.gender || '',
      },
      existingGuests,
      selectedGuestId: guests[i]?.selectedGuestId,
      errors: translateGuestErrors(guestErrorsArray?.[i]),
    }));
  }, [watchedGuests, guestCount, existingGuests, formErrors, translateGuestErrors]);

  const timeslotError = formErrors.arrivalTime
    ? t('error.fieldRequired', { label: t('spa.booking.timeslots.title') })
    : undefined;

  const genderOptions = useMemo(() => {
    const options = [
      { value: 'male', label: t('common.male') },
      { value: 'female', label: t('common.female') },
    ];
    if (showGenderNeutral) {
      options.push({ value: 'neutral', label: t('common.genderNeutral') });
    }
    return getGenderOptionsForRestriction(normaliseGenderCode(spaEntity?.genderSpecific), options)
      .genderOpts;
  }, [showGenderNeutral, spaEntity?.genderSpecific, t]);

  const handleGuestChange = (guestIndex: number, field: string, value: string) => {
    const currentGuests = form.getValues('guests') || [];
    const updatedGuests = [...currentGuests];
    if (!updatedGuests[guestIndex]) {
      updatedGuests[guestIndex] = { title: '', firstName: '', lastName: '', gender: '' };
    }
    updatedGuests[guestIndex] = { ...updatedGuests[guestIndex], [field]: value };
    form.setValue('guests', updatedGuests);
  };

  const handleExistingGuestSelect = useExistingGuestSelect({
    form,
    existingGuests,
  });

  const { handleFreeTextChange, preferenceCategories } = useNotesPreferences({ form });

  const handleTimeslotSelect = (timeslotValue: string) => {
    const daySlots = slotsByDate[selectedDateString];
    if (!daySlots) return;
    const slot = daySlots.find((s) => moment(s.startDateTime).format('HH:mm') === timeslotValue);
    if (slot) {
      form.setValue('arrivalTime', {
        startTime: moment(slot.startDateTime).format('HH:mm'),
        endTime: moment(slot.endDateTime).format('HH:mm'),
        startDateTime: moment(slot.startDateTime).format(),
        endDateTime: moment(slot.endDateTime).format(),
      });
    }
  };

  const hasExtras = !!(spaEntity?.extras?.length || spaEntity?.selectionGroups?.length);

  const { handleSubmit, handleNextStep, handleBack } = useSubmitTreatment({
    form,
    spaEntity: spaEntity || undefined,
    propertyId: property.id,
    language,
    id,
    onClose,
    hasExtras,
    onInvalid: scrollToFirstError,
  });
  const basePrice = spaEntity?.price?.amount || 0;
  const discountedPrice = spaEntity?.discount?.discountedPrice?.amount;
  const currencyCode = spaEntity?.price?.currencyCode || 'GBP';

  if (isLoadingEntity && !spaEntity) {
    return <Spinner isActive showDelayMessage />;
  }

  const isTelephoneOnly = spaEntity?.telephoneOnly || false;

  const staticMax = spaEntity?.maximumGuests ?? Infinity;
  const isSlotLimited =
    selectedSlot?.numberAvailable != null && selectedSlot.numberAvailable < staticMax;

  const guestLimitNote =
    requestedGuests > effectiveMaxGuests
      ? isSlotLimited
        ? t('spa.limitedAvailabilityNotice', { maxGuests: effectiveMaxGuests })
        : t('spa.maximumGuestsNotice', {
            spaEntity: t('spa.spaTreatment').toLowerCase(),
            maxGuests: effectiveMaxGuests,
          })
      : undefined;

  return (
    <>
      <Spinner showDelayMessage isActive={isSubmitting} />
      <SpaTreatmentFacilityBookingForm
        imageUrl={spaEntity?.image?.url}
        imageAlt={spaEntity?.name}
        onBack={handleBack}
        backLabel={t('spa.booking.goBack')}
        progressStep={1}
        progressTotal={hasExtras ? 2 : undefined}
        isLoading={calendarView === 'week' && isLoadingTimeslots}
        guestCount={guestCount}
        minGuests={minGuests}
        maxGuests={effectiveMaxGuests}
        guestLimitNote={guestLimitNote}
        onGuestCountChange={handleGuestCountChange}
        activeDate={weekStartDate}
        selectedDate={selectedDateString}
        calendarView={calendarView}
        availability={calendarView === 'week' ? availability : undefined}
        onDateSelect={handleDateSelect}
        onWeekNavigate={setWeekStartDate}
        onCalendarViewChange={handleCalendarViewChange}
        timeslots={hasNoAvailabilityAllWeek ? [] : timeslotChips}
        selectedTimeslot={watchedArrivalTime?.startTime}
        onTimeslotSelect={handleTimeslotSelect}
        timeslotError={timeslotError}
        guests={hasNoAvailabilityAllWeek ? [] : guestConfigs}
        onExistingGuestSelect={handleExistingGuestSelect}
        onGuestChange={handleGuestChange}
        titleOptions={['Mr', 'Mrs', 'Ms', 'Miss', 'Dr']}
        genderOptions={genderOptions}
        preferenceCategories={preferenceCategories}
        onPreferenceOptionToggle={() => {}}
        onPreferenceOtherChange={() => {}}
        onPreferenceFreeTextChange={handleFreeTextChange}
        telephoneOnly={isTelephoneOnly}
        unavailable={hasNoAvailabilityToday || false}
        labels={{
          title: spaEntity?.name || '',
          subtitle: t('spa.booking.subtitle'),
          noteText: t('spa.package.arrivalTime'),
          ...bookingLabels,
          stickyFooter: {
            totalLabel: bookingLabels.stickyFooterTotalLabel,
            ctaLabel: hasExtras ? t('spa.package.nextStep') : t('spa.book'),
          },
          telephoneOnly: bookingLabels.telephoneOnly,
          unavailable: bookingLabels.unavailable,
        }}
        stickyFooter={
          !isTelephoneOnly && !hasNoAvailabilityToday
            ? {
                title: spaEntity?.name || '',
                totalAmount:
                  discountedPrice != null ? discountedPrice * guestCount : basePrice * guestCount,
                originalAmount: discountedPrice != null ? basePrice * guestCount : undefined,
                currencyCode,
                onCtaClick: hasExtras ? handleNextStep : handleSubmit,
              }
            : undefined
        }
      />
    </>
  );
};
